import type { ExprNode, LValuePart } from "./ast";
import { evalExpr, type FunctionRegistry, type VariableAccess } from "./expr";

/**
 * 変数ストア — local / global を保持し、配列アクセス・代入をサポート。
 */
export class VariableStore implements VariableAccess {
  data: { local: Record<string, unknown>; global: Record<string, unknown> } = {
    local: {},
    global: {},
  };

  fns: FunctionRegistry;

  constructor(fns: FunctionRegistry) {
    this.fns = fns;
  }

  get(path: LValuePart[]): unknown {
    if (path.length === 0) return null;
    let cur: unknown = this.resolveRoot(path[0]!);
    for (let i = 1; i < path.length; i++) {
      if (cur === null || cur === undefined || typeof cur !== "object") return null;
      const part = path[i]!;
      const key = this.partKey(part);
      cur = (cur as Record<string | number, unknown>)[key];
    }
    return cur ?? null;
  }

  set(path: LValuePart[], value: unknown): void {
    if (path.length === 0) throw new Error("空のパスへの代入");
    // 単一名: local.x = 5 を `local.x` と書く必要がある。bare の場合は `local.bare` 扱い。
    if (path.length === 1) {
      const first = path[0]!;
      if (first.kind !== "name") throw new Error("配列単独への代入は未対応");
      this.data.local[first.name] = value;
      return;
    }
    const rootPart = path[0]!;
    if (rootPart.kind !== "name") throw new Error("root は識別子である必要があります");
    const rootName = rootPart.name;
    let root: Record<string, unknown>;
    if (rootName === "global") {
      root = this.data.global;
    } else if (rootName === "local") {
      root = this.data.local;
    } else {
      // bare identifier はネストすると意味不明。 local 配下に置く
      if (!this.data.local[rootName]) this.data.local[rootName] = {};
      root = this.data.local[rootName] as Record<string, unknown>;
    }

    let cur: Record<string | number, unknown> = root as Record<string | number, unknown>;
    for (let i = 1; i < path.length - 1; i++) {
      const part = path[i]!;
      const key = this.partKey(part);
      const next = cur[key];
      const nextPart = path[i + 1]!;
      if (next === undefined || next === null || typeof next !== "object") {
        cur[key] = nextPart.kind === "index" ? [] : {};
      }
      cur = cur[key] as Record<string | number, unknown>;
    }
    const last = path[path.length - 1]!;
    cur[this.partKey(last)] = value;
  }

  unset(path: LValuePart[]): void {
    if (path.length === 0) return;
    if (path.length === 1) {
      const first = path[0]!;
      if (first.kind === "name") delete this.data.local[first.name];
      return;
    }
    const parent = this.get(path.slice(0, -1));
    const last = path[path.length - 1]!;
    if (parent && typeof parent === "object") {
      delete (parent as Record<string | number, unknown>)[this.partKey(last)];
    }
  }

  private resolveRoot(part: LValuePart): unknown {
    if (part.kind !== "name") return null;
    const name = part.name;
    if (name === "local") return this.data.local;
    if (name === "global") return this.data.global;
    // bare identifier → local.{name}
    return this.data.local[name];
  }

  private partKey(part: LValuePart): string | number {
    if (part.kind === "name") return part.name;
    const v = evalExpr(part.expr, this, this.fns);
    if (typeof v === "string" || typeof v === "number") return v;
    return String(v);
  }

  // セーブ・ロード用のシリアライズ
  serialize(): { local: Record<string, unknown>; global: Record<string, unknown> } {
    return JSON.parse(JSON.stringify(this.data));
  }

  hydrate(state: { local?: Record<string, unknown>; global?: Record<string, unknown> }): void {
    if (state.local) this.data.local = state.local;
    if (state.global) this.data.global = state.global;
  }
}

/**
 * 組み込み関数の標準セット。
 */
export class StandardFunctions implements FunctionRegistry {
  private fns: Record<string, (...args: unknown[]) => unknown> = {
    len: (x: unknown) => {
      if (typeof x === "string") return [...x].length;
      if (Array.isArray(x)) return x.length;
      if (x && typeof x === "object") return Object.keys(x).length;
      return 0;
    },
    random: (n: unknown) => Math.floor(Math.random() * Number(n)),
    random_range: (min: unknown, max: unknown) =>
      Math.floor(Math.random() * (Number(max) - Number(min) + 1)) + Number(min),
    floor: (x: unknown) => Math.floor(Number(x)),
    ceil: (x: unknown) => Math.ceil(Number(x)),
    round: (x: unknown) => Math.round(Number(x)),
    abs: (x: unknown) => Math.abs(Number(x)),
    min: (...xs: unknown[]) => Math.min(...xs.map((v) => Number(v))),
    max: (...xs: unknown[]) => Math.max(...xs.map((v) => Number(v))),
    clamp: (x: unknown, lo: unknown, hi: unknown) =>
      Math.min(Math.max(Number(x), Number(lo)), Number(hi)),
    exists: () => false, // 静的には判定できない、ランタイムでオーバーライド可
    to_string: (x: unknown) => String(x ?? ""),
    to_number: (x: unknown) => Number(x),
    to_bool: (x: unknown) => Boolean(x),
  };

  register(name: string, fn: (...args: unknown[]) => unknown): void {
    this.fns[name] = fn;
  }

  call(name: string, args: unknown[]): unknown {
    const fn = this.fns[name];
    if (!fn) throw new Error(`未定義の関数: ${name}`);
    return fn(...args);
  }
}

// 式評価のヘルパー(外部 API)
export function evaluate(
  expr: ExprNode,
  vars: VariableAccess,
  fns: FunctionRegistry,
): unknown {
  return evalExpr(expr, vars, fns);
}
